// Strumień SSE przez fetch (jedna droga dla przeglądarki i aplikacji ze sklepu): EventSource nie wysyła nagłówków, a
// aplikacja podaje żeton w Authorization (api/adres.ts, docs/pwa/NATYWNA.md 3). Zachowanie jak EventSource (HTML, SSE):
// zerwanie albo koniec odpowiedzi - „error” i ponowienie po `retry` (domyślnie 3 s) z Last-Event-ID; odpowiedź inna niż
// 200 text/event-stream - „error” i koniec na stałe (CLOSED). Wiadomości jako MessageEvent z nazwą z pola `event`.
// Ponad EventSource: cisza dłuższa niż CISZA_MS (brama podtrzymuje co 30 s, worker-go/brama/sse.go) = martwe połączenie
// (telefon zamroził aplikację w tle i zerwał TCP bez błędu, odczyt czekałby bez końca) - przerwanie i ponowienie.
import { adres, naglowki } from "../api/adres";

const CISZA_MS = 70_000;

export class Strumien extends EventTarget {
  static readonly CONNECTING = 0;
  static readonly OPEN = 1;
  static readonly CLOSED = 2;

  readyState: number = Strumien.CONNECTING;
  private ostatnie = "";
  private ponowZa = 3000;
  private przerwij: AbortController | null = null;
  private zegar = 0;

  constructor(private readonly sciezka: string) {
    super();
    void this.polacz();
  }

  close(): void {
    this.readyState = Strumien.CLOSED;
    window.clearTimeout(this.zegar);
    this.przerwij?.abort();
  }

  private async polacz(): Promise<void> {
    if (this.readyState === Strumien.CLOSED) return;
    this.readyState = Strumien.CONNECTING;
    const ac = new AbortController();
    this.przerwij = ac;
    let odp: Response;
    try {
      odp = await fetch(adres(this.sciezka), {
        headers: await naglowki({ Accept: "text/event-stream", ...(this.ostatnie ? { "Last-Event-ID": this.ostatnie } : {}) }),
        cache: "no-store",
        signal: ac.signal,
      });
    } catch {
      return this.zerwane();
    }
    if (this.readyState === Strumien.CLOSED) return;
    if (!odp.ok || !odp.body || !(odp.headers.get("Content-Type") ?? "").startsWith("text/event-stream")) {
      this.readyState = Strumien.CLOSED;
      this.dispatchEvent(new Event("error"));
      return;
    }
    this.readyState = Strumien.OPEN;
    this.dispatchEvent(new Event("open"));
    try {
      await this.czytaj(odp.body, ac);
    } catch { /* zerwane w trakcie albo przerwane przez strażnika ciszy */ }
    this.zerwane();
  }

  private zerwane(): void {
    if (this.readyState === Strumien.CLOSED) return;
    this.readyState = Strumien.CONNECTING;
    this.dispatchEvent(new Event("error"));
    if (this.readyState !== Strumien.CLOSED) this.zegar = window.setTimeout(() => void this.polacz(), this.ponowZa);
  }

  private async czytaj(tresc: ReadableStream<Uint8Array>, ac: AbortController): Promise<void> {
    const r = tresc.getReader();
    const dekoder = new TextDecoder();
    let bufor = "";
    let typ = "";
    let dane: string[] = [];
    let cisza = 0;
    try {
      for (;;) {
        window.clearTimeout(cisza);
        cisza = window.setTimeout(() => ac.abort(), CISZA_MS);
        const { value, done } = await r.read();
        if (done || this.readyState === Strumien.CLOSED) return;
        bufor += dekoder.decode(value, { stream: true });
        let i: number;
        while ((i = bufor.indexOf("\n")) >= 0) {
          const linia = bufor.slice(0, i).replace(/\r$/, "");
          bufor = bufor.slice(i + 1);
          if (linia === "") { // koniec wiadomości
            if (dane.length) this.dispatchEvent(new MessageEvent(typ || "message", { data: dane.join("\n"), lastEventId: this.ostatnie }));
            typ = "";
            dane = [];
            continue;
          }
          if (linia.startsWith(":")) continue; // podtrzymanie
          const d = linia.indexOf(":");
          const pole = d < 0 ? linia : linia.slice(0, d);
          const wartosc = d < 0 ? "" : linia.slice(d + 1).replace(/^ /, "");
          if (pole === "event") typ = wartosc;
          else if (pole === "data") dane.push(wartosc);
          else if (pole === "id" && !wartosc.includes("\0")) this.ostatnie = wartosc;
          else if (pole === "retry" && /^\d+$/.test(wartosc)) this.ponowZa = Number(wartosc);
        }
      }
    } finally {
      window.clearTimeout(cisza);
    }
  }
}
